import { useState, useEffect, useCallback } from "react";
import { FaPrint, FaTrash, FaMoneyBillWave } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import { HiXMark } from "react-icons/hi2";
import { PrintLayout } from "../PrintLayout";
import ConfirmActionModal from "../ConfirmDeleteModal";

// --- CONFIGURATION ---
const API_KEY =
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const BASE_URL = "https://api.muktihospital.com/api/medicine-bill";

const PharmacyBillAddPaymentModal = ({
  isOpen,
  onClose,
  bill: initialBill,
  onPaymentSuccess,
}) => {
  const [billDetails, setBillDetails] = useState(initialBill);
  const [transactions, setTransactions] = useState(initialBill?.payments || []);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState({
    show: false,
    paymentId: null,
  });
  const [printData, setPrintData] = useState(null);

  // Form State
  const [paymentForm, setPaymentForm] = useState({
    date: new Date().toISOString().split("T")[0],
    amount: "",
    paymentMode: "Cash",
    note: "",
  });

  // 1. Fetch Latest Data
  const fetchLatestData = useCallback(async () => {
    if (!initialBill?.id) return;
    try {
      const res = await axios.get(`${BASE_URL}/${initialBill.id}`, {
        headers: { "x-api-key": API_KEY },
      });
      const fullData = res.data.bill;

      setBillDetails(fullData);
      setTransactions(fullData.payments || []);

      // Calculate Due
      const net = parseFloat(fullData.netAmount) || 0;
      const paid = parseFloat(fullData.paymentAmount) || 0;
      const due = net - paid;

      setPaymentForm((prev) => ({
        ...prev,
        amount: due > 0 ? due : "",
      }));
    } catch (error) {
      console.error("Error fetching bill data:", error);
    }
  }, [initialBill?.id]);

  useEffect(() => {
    if (isOpen && initialBill) {
      setBillDetails(initialBill);
      setTransactions(initialBill.payments || []);

      // Initial calc
      const net = parseFloat(initialBill.netAmount) || 0;
      const paid = parseFloat(initialBill.paymentAmount) || 0;
      const due = net - paid;

      setPaymentForm((prev) => ({
        ...prev,
        amount: due > 0 ? due : "",
      }));

      fetchLatestData();
    }
  }, [isOpen, initialBill, fetchLatestData]);

  // 2. Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    const remainingDue =
      (billDetails?.netAmount || 0) - (billDetails?.paymentAmount || 0);
    if (
      parseFloat(paymentForm.amount || 0) > remainingDue &&
      remainingDue > 0
    ) {
      toast.warning("Warning: Payment exceeds due amount.");
    }

    setIsSubmitting(true);
    try {
      const payload = {
        billId: billDetails.id,
        paymentDate: paymentForm.date,
        amount: parseFloat(paymentForm.amount || 0),
        paymentMode: paymentForm.paymentMode,
        note: paymentForm.note,
      };

      await axios.post(`${BASE_URL}/add-payment`, payload, {
        headers: { "x-api-key": API_KEY },
      });

      toast.success("Payment Received!");
      if (onPaymentSuccess) onPaymentSuccess();
      await fetchLatestData();
      setPaymentForm((prev) => ({ ...prev, amount: "", note: "" }));
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to add payment");
    } finally {
      setIsSubmitting(false);
    }
  };

  // 3. Delete Handler
  const initiateDelete = (id) =>
    setConfirmDelete({ show: true, paymentId: id });

  const executeDelete = async () => {
    if (!confirmDelete.paymentId) return;
    try {
      await axios.delete(
        `${BASE_URL}/delete-payment/${confirmDelete.paymentId}`,
        {
          headers: { "x-api-key": API_KEY },
        },
      );
      toast.success("Transaction Deleted!");
      if (onPaymentSuccess) onPaymentSuccess();
      await fetchLatestData();
      setConfirmDelete({ show: false, paymentId: null });
    } catch (error) {
      toast.error("Failed to delete transaction.");
    }
  };

  // 4. Print Handler
  const handlePrintReceipt = (tx) => {
    setPrintData({
      title: "Pharmacy Payment Receipt",
      billInfo: (
        <>
          <div>
            <p>
              <strong>Receipt No:</strong> {tx.id ? `PAY-${tx.id}` : "N/A"}
            </p>
            <p>
              <strong>Date:</strong>{" "}
              {new Date(tx.paymentDate).toLocaleDateString("en-GB")}
            </p>
            <p>
              <strong>Payment Mode:</strong> {tx.paymentMode}
            </p>
          </div>
          <div>
            <p>
              <strong>Bill No:</strong> {billDetails.billNumber}
            </p>
            <p>
              <strong>Customer:</strong> {billDetails.customerName || "N/A"}
            </p>
            <p>
              <strong>Phone:</strong> {billDetails.customerPhone || "N/A"}
            </p>
          </div>
        </>
      ),
      table: (
        <tbody>
          <tr>
            <td style={{ fontWeight: "bold", borderBottom: "1px solid #ddd" }}>
              Description
            </td>
            <td
              style={{
                textAlign: "right",
                fontWeight: "bold",
                borderBottom: "1px solid #ddd",
              }}
            >
              Amount
            </td>
          </tr>
          <tr>
            <td style={{ paddingTop: "8px" }}>
              Payment Received ({tx.paymentMode})
            </td>
            <td style={{ textAlign: "right", paddingTop: "8px" }}>
              ৳ {parseFloat(tx.amount).toFixed(2)}
            </td>
          </tr>
          {tx.note && (
            <tr>
              <td
                colSpan="2"
                style={{ fontSize: "0.9em", color: "#555", paddingTop: "4px" }}
              >
                Note: {tx.note}
              </td>
            </tr>
          )}
        </tbody>
      ),
      summary: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            fontWeight: "bold",
            marginTop: "10px",
          }}
        >
          <span>Total Received</span>
          <span>৳ {parseFloat(tx.amount).toFixed(2)}</span>
        </div>
      ),
    });
  };

  // Print Effect Logic
  useEffect(() => {
    if (printData) {
      const timer = setTimeout(() => {
        const printLayout = document.querySelector(".payment-history-print");
        if (printLayout) {
          printLayout.style.display = "block";
          window.print();
          setTimeout(() => {
            printLayout.style.display = "none";
            setPrintData(null);
          }, 500);
        } else {
          setPrintData(null);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [printData]);

  if (!isOpen) return null;

  const activeBill = billDetails || initialBill || {};

  const currentTotal = parseFloat(activeBill.netAmount || 0);
  const currentPaid = parseFloat(activeBill.paymentAmount || 0);
  const currentDue = currentTotal - currentPaid;

  // Helper Component for consistency
  const InfoRow = ({ label, value, isBold, className = "" }) => (
    <div
      className={`flex justify-between border-b border-gray-100 py-2 text-sm last:border-0 ${className}`}
    >
      <span className="text-gray-500 font-medium">{label}</span>
      <span className={`text-right text-gray-800 ${isBold ? "font-bold" : ""}`}>
        {value || "-"}
      </span>
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 z-40 flex items-start sm:items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0 overflow-y-auto print-hidden">
        <div className="bg-white shadow-lg w-full my-4 sm:my-0 max-h-[100vh] h-full overflow-y-auto">
          {/* Header */}
          <div className="flex justify-between items-center p-4 bg-gray-200 rounded-t-lg border-b border-gray-300">
            <h2 className="text-lg font-bold">Manage Pharmacy Payments</h2>
            <button
              onClick={onClose}
              className="size-7 rounded-full bg-white text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
            >
              <HiXMark />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* LEFT: Bill Details */}
              <div className="lg:col-span-4 space-y-4">
                <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
                  <h3 className="text-gray-700 font-bold border-b pb-2 mb-3 text-sm uppercase">
                    Bill Summary
                  </h3>
                  <InfoRow
                    label="Bill No"
                    value={activeBill.billNumber}
                    isBold
                  />
                  <InfoRow
                    label="Bill Date"
                    value={
                      activeBill.createdAt
                        ? new Date(activeBill.createdAt).toLocaleDateString()
                        : "-"
                    }
                  />
                  <InfoRow
                    label="Total Amount"
                    value={`৳ ${currentTotal.toLocaleString()}`}
                  />
                  <InfoRow
                    label="Total Paid"
                    value={`৳ ${currentPaid.toLocaleString()}`}
                    isBold
                  />
                  <div className="flex justify-between py-2 text-sm font-bold text-red-600 border-t mt-2">
                    <span>Due Amount</span>
                    <span>
                      ৳ {currentDue > 0 ? currentDue.toFixed(2) : "0.00"}
                    </span>
                  </div>
                </div>

                <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
                  <h3 className="text-gray-700 font-bold border-b pb-2 mb-3 text-sm uppercase">
                    Customer Information
                  </h3>
                  <InfoRow
                    label="Customer Name"
                    value={
                      activeBill.customerName || activeBill.patientName || "N/A"
                    }
                  />
                  <InfoRow
                    label="Phone"
                    value={
                      activeBill.customerPhone ||
                      activeBill.patientPhone ||
                      "N/A"
                    }
                  />
                  <InfoRow
                    label="Address"
                    value={activeBill.customerAddress || "N/A"}
                  />
                </div>
              </div>

              {/* RIGHT: FORM & HISTORY */}
              <div className="lg:col-span-8 space-y-6">
                {/* 1. PAYMENT FORM (Green Style) */}
                <div className="bg-white p-5 rounded-lg shadow-sm border-l-4 border-[#73CB64]">
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2 border-b pb-2">
                    <FaMoneyBillWave className="text-[#73CB64]" /> Receive
                    Payment
                  </h3>
                  <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="text-xs font-bold block mb-1">
                          Date
                        </label>
                        <input
                          type="date"
                          className="w-full border p-2 rounded text-sm"
                          value={paymentForm.date}
                          onChange={(e) =>
                            setPaymentForm({
                              ...paymentForm,
                              date: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold block mb-1">
                          Amount (৳)
                        </label>
                        <input
                          type="number"
                          placeholder="0.00"
                          className="w-full border p-2 rounded text-sm focus:border-[#73CB64] outline-none"
                          value={paymentForm.amount}
                          onChange={(e) =>
                            setPaymentForm({
                              ...paymentForm,
                              amount: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="flex gap-3 mb-3">
                      <div className="flex-1">
                        <label className="text-xs font-bold block mb-1">
                          Mode
                        </label>
                        <select
                          className="w-full border p-2 rounded text-sm"
                          value={paymentForm.paymentMode}
                          onChange={(e) =>
                            setPaymentForm({
                              ...paymentForm,
                              paymentMode: e.target.value,
                            })
                          }
                        >
                          <option>Cash</option>
                          <option>Cheque</option>
                          <option>Card</option>
                          <option>UPI</option>
                        </select>
                      </div>
                      <div className="flex-[2]">
                        <label className="text-xs font-bold block mb-1">
                          Note
                        </label>
                        <input
                          type="text"
                          className="w-full border p-2 rounded text-sm"
                          value={paymentForm.note}
                          onChange={(e) =>
                            setPaymentForm({
                              ...paymentForm,
                              note: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="bg-[#73CB64] text-white px-5 py-2 rounded text-sm hover:bg-M-primary-color font-medium shadow-sm transition"
                      >
                        {isSubmitting ? "..." : "Receive Payment"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            {/* 2. TRANSACTION HISTORY */}
            <div className="mt-8 bg-white p-5 rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-gray-800 font-bold border-b pb-3 mb-4 text-sm uppercase">
                Transaction History
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left border border-gray-200">
                  <thead className="bg-gray-100 text-gray-600 uppercase text-xs">
                    <tr>
                      <th className="px-4 py-3 font-semibold border-b">ID</th>
                      <th className="px-4 py-3 font-semibold border-b">Date</th>
                      <th className="px-4 py-3 font-semibold border-b">
                        Details
                      </th>
                      <th className="px-4 py-3 font-semibold border-b text-right">
                        Amount (৳)
                      </th>
                      <th className="px-4 py-3 font-semibold border-b text-center">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 text-gray-700">
                    {transactions.length > 0 ? (
                      transactions.map((tx, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono text-xs text-gray-500">
                            {tx.id ? `PAY-${tx.id}` : "-"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {new Date(tx.paymentDate).toLocaleDateString(
                              "en-GB",
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col">
                              <span className="text-xs font-bold text-[#73CB64] flex items-center gap-1">
                                <FaMoneyBillWave size={10} /> Payment Received
                              </span>
                              <span className="text-[10px] text-gray-500 mt-0.5">
                                Via {tx.paymentMode}{" "}
                                {tx.note ? `- ${tx.note}` : ""}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-sm text-[#73CB64]">
                            {parseFloat(tx.amount).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-center flex justify-center gap-2">
                            <button
                              onClick={() => handlePrintReceipt(tx)}
                              className="text-blue-500 hover:text-blue-700 transition"
                              title="Print"
                            >
                              <FaPrint />
                            </button>
                            <button
                              onClick={() => initiateDelete(tx.id)}
                              className="text-red-500 hover:text-red-700 transition"
                              title="Delete"
                            >
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="5"
                          className="px-4 py-8 text-center text-gray-400 italic"
                        >
                          No payments found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden Print Area */}
      {printData && (
        <PrintLayout
          className="payment-history-print"
          title={printData.title}
          billInfo={printData.billInfo}
          table={printData.table}
          summary={printData.summary}
          footer={printData.footer}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmActionModal
        isOpen={confirmDelete.show}
        onClose={() => setConfirmDelete({ show: false, paymentId: null })}
        onConfirm={executeDelete}
        ids={[confirmDelete.paymentId]}
        entityName="payment"
        title="Delete Payment"
        message="Are you sure? This will revert the bill balance."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export default PharmacyBillAddPaymentModal;
